import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Search } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export default function AssignQuoteToOrgModal({ open, quote, onClose, onAssigned }) {
  const { API } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setSelectedOrg(null);
      setSearch("");
      setError("");
      return;
    }
    setLoading(true);
    axios
      .get(`${API}/admin/organizations`, { withCredentials: true })
      .then(({ data }) => setOrgs(data.organizations || []))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [API, open]);

  if (!open || !quote) return null;

  const filtered = orgs.filter((o) => o.name.toLowerCase().includes(search.trim().toLowerCase()));
  const currentQuoteId = selectedOrg?.billing?.quote_id || null;

  const confirmAssign = async () => {
    setConfirming(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/admin/quotes/${quote.quote_id}/assign-to-organization`,
        { org_id: selectedOrg.org_id },
        { withCredentials: true },
      );
      onAssigned?.(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/40 p-4" data-testid="assign-quote-org-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-[#111C33]">
        {!selectedOrg ? (
          <>
            <h3 className="text-base font-bold text-slate-950 dark:text-white">Assign to Existing Organization</h3>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Choose the organization that {quote.quote_id} should be assigned to.</p>
            <label className="relative mt-4 block">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search organizations…"
                data-testid="assign-quote-org-search"
                className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-4 text-sm focus:border-blue-600 focus:outline-none dark:border-white/15 dark:bg-white/5 dark:text-white"
              />
            </label>
            <div className="mt-3 max-h-64 space-y-1.5 overflow-y-auto">
              {loading ? (
                <p className="py-4 text-center text-sm font-semibold text-slate-400">Loading organizations…</p>
              ) : filtered.length === 0 ? (
                <p className="py-4 text-center text-sm font-semibold text-slate-400">No organizations found.</p>
              ) : (
                filtered.map((o) => (
                  <button
                    key={o.org_id}
                    type="button"
                    onClick={() => setSelectedOrg(o)}
                    data-testid={`assign-quote-org-option-${o.org_id}`}
                    className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-left text-sm hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5"
                  >
                    <span className="font-bold text-slate-950 dark:text-white">{o.name}</span>
                    <span className="text-xs text-slate-400">{o.billing?.quote_id ? `On ${o.billing.quote_id}` : "No active quote"}</span>
                  </button>
                ))
              )}
            </div>
            <div className="mt-5 flex justify-end">
              <button type="button" onClick={onClose} data-testid="assign-quote-org-cancel-btn" className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 className="text-base font-bold text-slate-950 dark:text-white" data-testid="assign-quote-confirm-title">
              {currentQuoteId ? "Replace Current Billing Quote?" : "Assign This Quote?"}
            </h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400" data-testid="assign-quote-confirm-body">
              {currentQuoteId ? (
                <>
                  <strong>{selectedOrg.name}</strong> is currently using <strong>{currentQuoteId}</strong>. Assigning <strong>{quote.quote_id}</strong> will replace {currentQuoteId} as the organization's active billing quote.
                </>
              ) : (
                <>
                  <strong>{quote.quote_id}</strong> will become <strong>{selectedOrg.name}</strong>'s active billing quote.
                </>
              )}
            </p>
            {error && <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="assign-quote-error">{error}</p>}
            <div className="mt-5 flex items-center gap-3">
              <button
                type="button"
                onClick={confirmAssign}
                disabled={confirming}
                data-testid="assign-quote-confirm-btn"
                className="inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {confirming && <Loader2 size={14} className="animate-spin" />}
                {currentQuoteId ? "Replace Quote" : "Assign Quote"}
              </button>
              <button
                type="button"
                onClick={() => setSelectedOrg(null)}
                disabled={confirming}
                data-testid="assign-quote-back-btn"
                className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
